import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import api from "../api/client";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";

const TYPES = [
  { v: "appartement", l: "Appartement" },
  { v: "maison", l: "Maison" },
  { v: "studio", l: "Studio" },
  { v: "duplex", l: "Duplex" },
  { v: "villa", l: "Villa" },
  { v: "local_commercial", l: "Local commercial" },
];

const CATEGORIES = [
  { v: "", l: "Standard" },
  { v: "confort", l: "Confort" },
  { v: "premium", l: "Premium" },
  { v: "luxe", l: "Luxe" },
];

const STATUTS = [
  { v: "disponible", l: "Disponible" },
  { v: "reserve", l: "Réservé" },
  { v: "vendu", l: "Vendu" },
  { v: "loue", l: "Loué" },
  { v: "travaux", l: "En travaux" },
];

const EQUIPEMENTS_DISPO = [
  "climatisation", "chauffage", "ascenseur", "parking", "balcon", "terrasse", "jardin",
  "piscine", "garage", "cave", "cuisine_equipee", "buanderie", "vidéosurveillance",
  "interphone", "gardien", "fibre_optique",
];

export default function UniteEditDialog({ open, onClose, unite, onUpdated }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && unite) {
      setForm({
        nom: unite.nom || "",
        numero_lot: unite.numero_lot || "",
        type_unite: unite.type_unite || "appartement",
        etage: unite.etage ?? "",
        categorie: unite.categorie || "",
        surface_m2: unite.surface_m2 || "",
        nb_chambres: unite.nb_chambres || "",
        nb_salles_bain: unite.nb_salles_bain || "",
        prix_vente: unite.prix_vente || "",
        prix_location: unite.prix_location || "",
        statut: unite.statut || "disponible",
        description: unite.description || "",
        equipements: unite.equipements || [],
      });
    }
  }, [open, unite]);

  const toggleEquipement = (eq) => {
    const list = form.equipements || [];
    setForm({ ...form, equipements: list.includes(eq) ? list.filter((e) => e !== eq) : [...list, eq] });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Cast numerics
      const payload = {
        ...form,
        etage: form.etage === "" ? null : Number(form.etage),
        surface_m2: form.surface_m2 ? Number(form.surface_m2) : null,
        nb_chambres: form.nb_chambres ? Number(form.nb_chambres) : null,
        nb_salles_bain: form.nb_salles_bain ? Number(form.nb_salles_bain) : null,
        prix_vente: form.prix_vente ? Number(form.prix_vente) : null,
        prix_location: form.prix_location ? Number(form.prix_location) : null,
      };
      await api.patch(`/unites/${unite.id}`, payload);
      toast.success("Unité mise à jour");
      onUpdated?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  if (!unite) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-white max-w-2xl p-0 overflow-hidden max-h-[90vh] flex flex-col" data-testid="unite-edit-dialog">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-[#E2E5F1] shrink-0">
          <DialogTitle className="font-display text-xl text-[#0F1A3D]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Modifier l'unité · {unite.nom}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="overflow-y-auto flex-1 px-6 py-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Nom / Référence"><Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="ue-nom" /></Field>
            <Field label="N° de lot"><Input value={form.numero_lot} onChange={(e) => setForm({ ...form, numero_lot: e.target.value })} placeholder="Ex: 42" data-testid="ue-numero-lot" /></Field>
            <Field label="Type">
              <Select value={form.type_unite} onValueChange={(v) => setForm({ ...form, type_unite: v })}>
                <SelectTrigger className="h-10" data-testid="ue-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Étage"><Input type="number" value={form.etage} onChange={(e) => setForm({ ...form, etage: e.target.value })} /></Field>
            <Field label="Catégorie">
              <Select value={form.categorie || "standard"} onValueChange={(v) => setForm({ ...form, categorie: v })}>
                <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="confort">Confort</SelectItem>
                  <SelectItem value="premium">Premium</SelectItem>
                  <SelectItem value="luxe">Luxe</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Statut">
              <Select value={form.statut} onValueChange={(v) => setForm({ ...form, statut: v })}>
                <SelectTrigger className="h-10" data-testid="ue-statut"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Surface (m²)"><Input type="number" value={form.surface_m2} onChange={(e) => setForm({ ...form, surface_m2: e.target.value })} /></Field>
            <Field label="Chambres"><Input type="number" value={form.nb_chambres} onChange={(e) => setForm({ ...form, nb_chambres: e.target.value })} /></Field>
            <Field label="Salles de bain"><Input type="number" value={form.nb_salles_bain} onChange={(e) => setForm({ ...form, nb_salles_bain: e.target.value })} /></Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Prix vente (FCFA)"><Input type="number" value={form.prix_vente} onChange={(e) => setForm({ ...form, prix_vente: e.target.value })} data-testid="ue-prix" /></Field>
            <Field label="Loyer mensuel (FCFA)"><Input type="number" value={form.prix_location} onChange={(e) => setForm({ ...form, prix_location: e.target.value })} /></Field>
          </div>

          <Field label="Description">
            <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 text-sm rounded-md border border-[#E2E5F1] focus:outline-none focus:border-[#1E3A8A] resize-none" />
          </Field>

          <div>
            <Label className="text-[10px] uppercase tracking-wider text-[#4B5775] font-bold mb-2 block">Équipements</Label>
            <div className="flex flex-wrap gap-2">
              {EQUIPEMENTS_DISPO.map((eq) => {
                const on = (form.equipements || []).includes(eq);
                return (
                  <button
                    key={eq}
                    type="button"
                    onClick={() => toggleEquipement(eq)}
                    className={`text-[11px] px-2.5 py-1 rounded-full border-2 transition capitalize ${on ? "border-[#1E3A8A] bg-[#E8EBFA] text-[#1E3A8A] font-bold" : "border-[#E2E5F1] text-[#4B5775] hover:border-[#1E3A8A]/40"}`}
                  >
                    {eq.replace(/_/g, " ")}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex gap-2 pt-3 border-t border-[#E2E5F1] -mx-6 px-6 sticky bottom-0 bg-white">
            <button type="button" onClick={onClose} className="flex-1 h-11 rounded-md border border-[#E2E5F1] text-xs font-bold uppercase tracking-wider text-[#4B5775]">Annuler</button>
            <button type="submit" disabled={saving} className="flex-1 h-11 rounded-md bg-[#1E3A8A] hover:bg-[#1E40AF] text-white text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="ue-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Enregistrer
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-[#4B5775] font-bold mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
